<script lang="ts" setup>
import type { Prop } from 'vue'
// components
import IconCircle from '@/components/Icons/IconCircle.vue'
import IconRing from '@/components/Icons/IconRing.vue'
// types
import { SelectedDiscount } from '@/types/basket'

const emit = defineEmits(['updateDiscountChoices'])
const props = defineProps({
  selectedDiscount: {
    type: String,
    required: true,
  } as Prop<SelectedDiscount>,
  confirmedDiscount: {
    type: String,
    required: true,
  } as Prop<SelectedDiscount | null>,
  bonusesCount: {
    type: Number,
    required: true,
  },
  spendAmountBonuses: {
    type: Number,
    required: true,
  },
  bonusSpendMessage: {
    type: String,
    required: true,
  },
  isAuthenticated: {
    type: Boolean,
    required: true,
  },
  basketDiscountInfoVisible: {
    type: Boolean,
    required: true,
  },
})

const bonusLabel = computed(() => {
  if (!props.isAuthenticated) {
    return 'Авторизуйтесь, чтобы использовать бонусы'
  }

  return `У вас ${
    props.bonusesCount !== 0 ? props.bonusesCount : 'нет'
  } бонусов`
})

const isPromocodeChoiceDisabled = computed(
  () => props.confirmedDiscount === SelectedDiscount.BONUSES,
)

const isPromocodeApplied = computed(() => {
  return (
    props.confirmedDiscount === SelectedDiscount.PROMOCODE &&
    props.bonusesCount > 0
  )
})

const isBonusChoiceDisabled = computed(
  () => props.bonusesCount === 0 || isPromocodeApplied.value,
)

const updateDiscountChoices = (value: SelectedDiscount) => {
  // Если кликаем по уже выбранной скидке
  if (value === props.selectedDiscount) {
    return
  }

  emit('updateDiscountChoices', value)
}
</script>

<template>
  <v-radio-group
    :model-value="selectedDiscount"
    :true-icon="IconCircle"
    :false-icon="IconRing"
    hide-details
  >
    <div class="discount-choices__radio">
      <v-radio
        label="Промокод"
        :disabled="isPromocodeChoiceDisabled"
        :value="SelectedDiscount.PROMOCODE"
        @click="updateDiscountChoices(SelectedDiscount.PROMOCODE)"
      />

      <div
        v-if="isPromocodeChoiceDisabled"
        class="discount-choices__text discount-choices__text--promocode"
      >
        Чтобы использовать промокод — отмените списание бонусов
      </div>
    </div>

    <div class="discount-choices__radio">
      <v-radio
        :label="bonusLabel"
        :disabled="isBonusChoiceDisabled || !isAuthenticated"
        :value="SelectedDiscount.BONUSES"
        @click="updateDiscountChoices(SelectedDiscount.BONUSES)"
      />

      <template v-if="isAuthenticated">
        <div v-if="isPromocodeApplied" class="discount-choices__text">
          Чтобы использовать бонусы — отмените промокод
        </div>

        <div
          v-if="!isBonusChoiceDisabled && !basketDiscountInfoVisible"
          class="discount-choices__bonus-info"
        >
          {{ bonusSpendMessage }}
        </div>
      </template>
    </div>
  </v-radio-group>
</template>

<style lang="scss" scoped>
.discount-choices__radio {
  --v-disabled-opacity: 0.5;
}

.discount-choices__text {
  margin-top: -4px;
  margin-left: 40px;
  font-size: 12px;
  font-weight: 600;
  opacity: 0.5;

  &--promocode {
    margin-bottom: 12px;
  }
}

.discount-choices__bonus-info {
  margin-left: 40px;
  font-size: 12px;
  font-weight: 600;
  color: $dark-grey;
}

:deep(.v-selection-control-group) {
  margin-left: -11px;
}
</style>
